import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ApiService from '../Services/ApiService';
import {AxiosRequestConfig} from 'axios';
import {STORAGE_KEYS} from '../Constants/AppConstants';
import LocalStorageService from '../Services/LocalStorageService';
import {useMessages} from './MessagesProvider';
import useEHSAuth from '../Hooks/useEHSAuth';

const POLL_INTERVAL_MS = 600000; // 10 minutes

type WriteStatusContextType = {
  hasIssues: boolean | null;
  loading: boolean;
};

const WriteStatusContext = createContext<WriteStatusContextType>({
  hasIssues: null,
  loading: true,
});

export const WriteStatusProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {addMessage, removeMessage} = useMessages();
  const [hasIssues, setHasIssues] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const {getUser} = useEHSAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Watch token state
  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = getUser().access_token;

      setToken(storedToken);
    };

    fetchToken();

    // setting up a listener to always have the latest token
    const interval = setInterval(fetchToken, 30000); // refresh token every 30s
    return () => clearInterval(interval);
  }, []);

  // Core polling logic
  const checkWriteStatus = async () => {
    try {
      let apiService = new ApiService(() => {});

      let config: AxiosRequestConfig = {};
      config.method = 'GET';
      config.url = process.env.REACT_APP_SYNC_STATUS_ENDPOINT;

      try {
        let response = await apiService.sendRequest<boolean>(config);

        setHasIssues(response?.data);

        if (response?.data) {
          addMessage({
            key: 'failedSyncWarning',
            type: 'warning',
            content:
              'There are unresolved sync errors with your account. Please contact us at support@ehsanalytics.com.',
            dismissible: true,
          });
        } else {
          removeMessage('failedSyncWarning');
        }
      } catch (err) {
        console.error('Sync check failed:', err);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Sync check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Control polling activation
  useEffect(() => {
    if (!token) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // First run immediately
    checkWriteStatus();

    // Then start interval
    intervalRef.current = setInterval(() => {
      if (token) checkWriteStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [token]);

  return (
    <WriteStatusContext.Provider value={{hasIssues, loading}}>
      {children}
    </WriteStatusContext.Provider>
  );
};

export const useWriteStatus = () => useContext(WriteStatusContext);
